import React, {useMemo, useState} from "react";
import {Table} from "react-bootstrap";
import {PiCaretDown, PiCaretUp, PiCaretUpDown} from "react-icons/pi";
import {formatNumber} from "../Statistics/format.js";
import {bereichLabel, formatDateTimeDE} from "./format.js";
import NutzungWidget from "./NutzungWidget.jsx";
import {BereichLink, SeiteLink} from "./Links.jsx";

const SPALTEN = [
    {key: "bereich", label: "Bereich", sortable: true, align: "start"},
    {key: "bezeichnung", label: "Bezeichnung", sortable: true, align: "start"},
    {key: "aufrufe", label: "Aufrufe", sortable: true, align: "end", format: formatNumber},
    {key: "nutzer", label: "Verschiedene Nutzer", sortable: true, align: "end", format: formatNumber},
    {key: "letzter", label: "Zuletzt benutzt", sortable: true, align: "start", format: formatDateTimeDE},
];

/**
 * Tabelle der genutzten Funktionen (Spezifikation Abschnitt 9,
 * /nutzung/daten/funktionen -> genutzt[]) - clientseitig sortierbar, exakt
 * dasselbe Muster wie NutzungMitarbeiterTabelle: der Endpunkt liefert alle
 * Zeilen des Zeitraums auf einmal (höchstens 300, serverseitig begrenzt),
 * keine Serverpaginierung nötig.
 */
export default function NutzungFunktionenTabelle({genutzt, loading, error}) {
    const [sort, setSort] = useState({key: "aufrufe", dir: "desc"});

    const rows = useMemo(() => {
        const liste = Array.isArray(genutzt) ? [...genutzt] : [];
        const {key, dir} = sort;
        liste.sort((a, b) => {
            let av = a[key];
            let bv = b[key];
            if (key === "bereich" || key === "bezeichnung") {
                av = bereichLabel(av) || av || "";
                bv = bereichLabel(bv) || bv || "";
                if (key === "bezeichnung") {
                    av = (a[key] || "").toLocaleLowerCase("de-DE");
                    bv = (b[key] || "").toLocaleLowerCase("de-DE");
                } else {
                    av = av.toLocaleLowerCase("de-DE");
                    bv = bv.toLocaleLowerCase("de-DE");
                }
            } else if (key === "letzter") {
                av = av ? new Date(av).getTime() : 0;
                bv = bv ? new Date(bv).getTime() : 0;
            } else {
                av = av || 0;
                bv = bv || 0;
            }
            if (av < bv) {
                return dir === "asc" ? -1 : 1;
            }
            if (av > bv) {
                return dir === "asc" ? 1 : -1;
            }
            return 0;
        });
        return liste;
    }, [genutzt, sort]);

    const toggleSort = (key) => {
        setSort((current) => {
            if (current.key !== key) {
                return {key, dir: key === "bereich" || key === "bezeichnung" ? "asc" : "desc"};
            }
            return {key, dir: current.dir === "asc" ? "desc" : "asc"};
        });
    };

    return (
        <NutzungWidget
            title="Genutzte Funktionen"
            help="Jede aufgerufene Route im gewählten Zeitraum - Bereich, Aufrufe und wie viele verschiedene Mitarbeiter sie genutzt haben. Höchstens die 300 meistgenutzten Funktionen."
            loading={loading}
            error={error}
            empty={!loading && !error && rows.length === 0}
            height={420}
        >
            <div style={{overflowX: "auto", maxHeight: 420, overflowY: "auto"}}>
                <Table hover size="sm" className="align-middle mb-0">
                    <thead>
                        <tr>
                            {SPALTEN.map((spalte) => (
                                <th
                                    key={spalte.key}
                                    className={spalte.align === "end" ? "text-end" : ""}
                                    style={spalte.sortable ? {cursor: "pointer"} : undefined}
                                    onClick={spalte.sortable ? () => toggleSort(spalte.key) : undefined}
                                >
                                    <span className="d-inline-flex align-items-center gap-1">
                                        {spalte.label}
                                        {spalte.sortable && (sort.key === spalte.key ? (
                                            sort.dir === "asc" ? <PiCaretUp aria-hidden="true" /> : <PiCaretDown aria-hidden="true" />
                                        ) : (
                                            <PiCaretUpDown className="text-muted" aria-hidden="true" />
                                        ))}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={`${row.bereich}-${row.route}`}>
                                <td>
                                    <BereichLink
                                        bereich={row.bereich}
                                        className="badge bg-body-secondary text-body border text-decoration-none"
                                    />
                                </td>
                                <td className="fw-semibold"><SeiteLink route={row.route} /></td>
                                <td className="text-end">{formatNumber(row.aufrufe)}</td>
                                <td className="text-end">{formatNumber(row.nutzer)}</td>
                                <td>{formatDateTimeDE(row.letzter)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </NutzungWidget>
    );
}
